import { z } from "zod"
import { BadgeElementV1 } from "./elements/Badge"
import { BulletElementV1, BulletSettingsV1 } from "./elements/Bullet"
import { ButtonElementV1, ButtonSettingsV1 } from "./elements/Button"
import { ButtonBaseElementV1 } from "./elements/ButtonBase"
import { CardElementV1 } from "./elements/Card"
import { CloseModalElementV1 } from "./elements/CloseModal"
import { ContainerElementV1, ContainerSettingsV1 } from "./elements/Container"
import { DivElementV1, DivSettingsV1 } from "./elements/Div"
import { DividerElementV1 } from "./elements/Divider"
import { DividingHeaderElementV1, DividingHeaderSettingsV1 } from "./elements/DividingHeader"
import { DropdownElementV1 } from "./elements/Dropdown"
import { HeadingElementV1, HeadingSettingsV1 } from "./elements/Heading"
import { HTMLElementV1, HTMLSettingsV1 } from "./elements/HTML"
import { ImageElementV1, ImageSettingsV1 } from "./elements/Image"
import { ImageBaseElementV1, ImageBaseSettingsV1 } from "./elements/ImageBase"
import { ListElementV1, ListSettingsV1, ListElementV2, ListSettingsV2 } from "./elements/List"
import { ListV2ElementV1, ListV2SettingsV1 } from "./elements/ListV2"
import { LottieElementV1, LottieSettingsV1 } from "./elements/Lottie"
import {
  MarqueeElementV1,
  MarqueeItemElementV1,
  MarqueeItemSettingsV1,
  MarqueeSettingsV1,
} from "./elements/Marquee"
import { ModalElementV1 } from "./elements/Modal"
import { ParagraphElementV1, ParagraphSettingsV1 } from "./elements/Paragraph"
import { RatingElementV1, RatingSettingsV1 } from "./elements/Rating"
import { ReviewElementV1, ReviewSettingsV1 } from "./elements/Review"
import { SectionElementV1 } from "./elements/Section"
import { SubheadingElementV1 } from "./elements/Subheading"
import { SubtitleElementV1, SubtitleSettingsV1 } from "./elements/Subtitle"
import { TableElementV1, TableSettingsV1 } from "./elements/Table"
import { TextElementV1 } from "./elements/Text"
import {
  TimelineElementV1,
  TimelineItemElementV1,
  TimelineItemSettingsV1,
} from "./elements/Timeline"
import { TitleElementV1, TitleSettingsV1 } from "./elements/Title"
import { VideoElementV1, VideoSettingsV1, VideoElementV2, VideoSettingsV2 } from "./elements/Video"
import { DrawerElementV1, DrawerSettingsV1 } from "./elements/Drawer"
import {
  ProductsElementV1,
  ProductsSettingsV1,
  ProductsElementV2,
  ProductsSettingsV2,
} from "./elements/Products"

// Version 1 ---------------------
export const ElementSettingsV1 = z.union([
  BulletSettingsV1,
  ButtonSettingsV1,
  ContainerSettingsV1,
  DivSettingsV1,
  DividingHeaderSettingsV1,
  HeadingSettingsV1,
  HTMLSettingsV1,
  ImageSettingsV1,
  ImageBaseSettingsV1,
  ListSettingsV1,
  LottieSettingsV1,
  MarqueeItemSettingsV1,
  MarqueeSettingsV1,
  ParagraphSettingsV1,
  RatingSettingsV1,
  ReviewSettingsV1,
  SubtitleSettingsV1,
  TableSettingsV1,
  TimelineItemSettingsV1,
  TitleSettingsV1,
  VideoSettingsV1,
  z.record(z.string()),
])

export type TElementSettingsV1 = z.infer<typeof ElementSettingsV1>

export const ElementV1 = z.discriminatedUnion("name", [
  BadgeElementV1,
  BulletElementV1,
  ButtonElementV1,
  ButtonBaseElementV1,
  CardElementV1,
  ContainerElementV1,
  DivElementV1,
  DividingHeaderElementV1,
  DropdownElementV1,
  HeadingElementV1,
  HTMLElementV1,
  ImageElementV1,
  ImageBaseElementV1,
  ListElementV1,
  LottieElementV1,
  MarqueeElementV1,
  MarqueeItemElementV1,
  ParagraphElementV1,
  RatingElementV1,
  ReviewElementV1,
  SectionElementV1,
  SubheadingElementV1,
  SubtitleElementV1,
  TableElementV1,
  TextElementV1,
  TimelineElementV1,
  TimelineItemElementV1,
  TitleElementV1,
  VideoElementV1,
])
export type TElementsV1 = z.infer<typeof ElementV1>

// Version 2 ---------------------
export const ElementSettingsV2 = z.union([
  BulletSettingsV1,
  ButtonSettingsV1,
  ContainerSettingsV1,
  DivSettingsV1,
  DividingHeaderSettingsV1,
  HeadingSettingsV1,
  HTMLSettingsV1,
  ImageSettingsV1,
  ImageBaseSettingsV1,
  ListSettingsV1,
  LottieSettingsV1,
  MarqueeItemSettingsV1,
  MarqueeSettingsV1,
  ParagraphSettingsV1,
  RatingSettingsV1,
  ReviewSettingsV1,
  SubtitleSettingsV1,
  TableSettingsV1,
  TimelineItemSettingsV1,
  TitleSettingsV1,
  VideoSettingsV2,
  z.record(z.string()),
])

export type TElementSettingsV2 = z.infer<typeof ElementSettingsV2>

export const ElementV2 = z.discriminatedUnion("name", [
  BadgeElementV1,
  BulletElementV1,
  ButtonElementV1,
  ButtonBaseElementV1,
  CardElementV1,
  ContainerElementV1,
  DivElementV1,
  DividingHeaderElementV1,
  DropdownElementV1,
  HeadingElementV1,
  HTMLElementV1,
  ImageElementV1,
  ImageBaseElementV1,
  ListElementV1,
  LottieElementV1,
  MarqueeElementV1,
  MarqueeItemElementV1,
  ParagraphElementV1,
  RatingElementV1,
  ReviewElementV1,
  SectionElementV1,
  SubheadingElementV1,
  SubtitleElementV1,
  TableElementV1,
  TextElementV1,
  TimelineElementV1,
  TimelineItemElementV1,
  TitleElementV1,
  VideoElementV2,
])
export type TElementV2 = z.infer<typeof ElementV2>

// Version 3 ---------------------
export const ElementSettingsV3 = z.union([
  BulletSettingsV1,
  ButtonSettingsV1,
  ContainerSettingsV1,
  DivSettingsV1,
  DividingHeaderSettingsV1,
  HeadingSettingsV1,
  HTMLSettingsV1,
  ImageSettingsV1,
  ImageBaseSettingsV1,
  ListSettingsV2,
  ListV2SettingsV1,
  LottieSettingsV1,
  MarqueeItemSettingsV1,
  MarqueeSettingsV1,
  ParagraphSettingsV1,
  RatingSettingsV1,
  ReviewSettingsV1,
  SubtitleSettingsV1,
  TableSettingsV1,
  TimelineItemSettingsV1,
  TitleSettingsV1,
  VideoSettingsV2,
  z.record(z.string()),
])

export type TElementSettingsV3 = z.infer<typeof ElementSettingsV3>

export const ElementV3 = z.discriminatedUnion("name", [
  BadgeElementV1,
  BulletElementV1,
  ButtonElementV1,
  ButtonBaseElementV1,
  CardElementV1,
  CloseModalElementV1,
  ContainerElementV1,
  DivElementV1,
  DividingHeaderElementV1,
  DropdownElementV1,
  HeadingElementV1,
  HTMLElementV1,
  ImageElementV1,
  ImageBaseElementV1,
  ListElementV2,
  ListV2ElementV1,
  LottieElementV1,
  MarqueeElementV1,
  MarqueeItemElementV1,
  ModalElementV1,
  ParagraphElementV1,
  RatingElementV1,
  ReviewElementV1,
  SectionElementV1,
  SubheadingElementV1,
  SubtitleElementV1,
  TableElementV1,
  TextElementV1,
  TimelineElementV1,
  TimelineItemElementV1,
  TitleElementV1,
  VideoElementV2,
])
export type TElementV3 = z.infer<typeof ElementV3>

// Version 4 ---------------------
export const ElementSettingsV4 = z.union([
  BulletSettingsV1,
  ButtonSettingsV1,
  ContainerSettingsV1,
  DivSettingsV1,
  DividingHeaderSettingsV1,
  DrawerSettingsV1,
  HeadingSettingsV1,
  HTMLSettingsV1,
  ImageSettingsV1,
  ImageBaseSettingsV1,
  ListSettingsV2,
  ListV2SettingsV1,
  LottieSettingsV1,
  MarqueeItemSettingsV1,
  MarqueeSettingsV1,
  ParagraphSettingsV1,
  ProductsSettingsV1,
  RatingSettingsV1,
  ReviewSettingsV1,
  SubtitleSettingsV1,
  TableSettingsV1,
  TimelineItemSettingsV1,
  TitleSettingsV1,
  VideoSettingsV2,
  z.record(z.string()),
])

export type TElementSettingsV4 = z.infer<typeof ElementSettingsV4>

export const ElementV4 = z.discriminatedUnion("name", [
  BadgeElementV1,
  BulletElementV1,
  ButtonElementV1,
  ButtonBaseElementV1,
  CardElementV1,
  CloseModalElementV1,
  ContainerElementV1,
  DivElementV1,
  DividerElementV1,
  DividingHeaderElementV1,
  DrawerElementV1,
  DropdownElementV1,
  HeadingElementV1,
  HTMLElementV1,
  ImageElementV1,
  ImageBaseElementV1,
  ListElementV2,
  ListV2ElementV1,
  LottieElementV1,
  MarqueeElementV1,
  MarqueeItemElementV1,
  ModalElementV1,
  ParagraphElementV1,
  ProductsElementV1,
  RatingElementV1,
  ReviewElementV1,
  SectionElementV1,
  SubheadingElementV1,
  SubtitleElementV1,
  TableElementV1,
  TextElementV1,
  TimelineElementV1,
  TimelineItemElementV1,
  TitleElementV1,
  VideoElementV2,
])
export type TElementV4 = z.infer<typeof ElementV4>

// Version 5 ---------------------
export const ElementSettingsV5 = z.union([
  BulletSettingsV1,
  ButtonSettingsV1,
  ContainerSettingsV1,
  DivSettingsV1,
  DividingHeaderSettingsV1,
  DrawerSettingsV1,
  HeadingSettingsV1,
  HTMLSettingsV1,
  ImageSettingsV1,
  ImageBaseSettingsV1,
  ListSettingsV2,
  ListV2SettingsV1,
  LottieSettingsV1,
  MarqueeItemSettingsV1,
  MarqueeSettingsV1,
  ParagraphSettingsV1,
  ProductsSettingsV2,
  RatingSettingsV1,
  ReviewSettingsV1,
  SubtitleSettingsV1,
  TableSettingsV1,
  TimelineItemSettingsV1,
  TitleSettingsV1,
  VideoSettingsV2,
  z.record(z.string()),
])

export type TElementSettingsV5 = z.infer<typeof ElementSettingsV5>

export const ElementV5 = z.discriminatedUnion("name", [
  BadgeElementV1,
  BulletElementV1,
  ButtonElementV1,
  ButtonBaseElementV1,
  CardElementV1,
  CloseModalElementV1,
  ContainerElementV1,
  DivElementV1,
  DividerElementV1,
  DividingHeaderElementV1,
  DrawerElementV1,
  DropdownElementV1,
  HeadingElementV1,
  HTMLElementV1,
  ImageElementV1,
  ImageBaseElementV1,
  ListElementV2,
  ListV2ElementV1,
  LottieElementV1,
  MarqueeElementV1,
  MarqueeItemElementV1,
  ModalElementV1,
  ParagraphElementV1,
  ProductsElementV2,
  RatingElementV1,
  ReviewElementV1,
  SectionElementV1,
  SubheadingElementV1,
  SubtitleElementV1,
  TableElementV1,
  TextElementV1,
  TimelineElementV1,
  TimelineItemElementV1,
  TitleElementV1,
  VideoElementV2,
])
export type TElementV5 = z.infer<typeof ElementV5>

// Latest ---------------------
export const ElementSettings = ElementSettingsV5
export type TElementSettings = TElementSettingsV5
export const Element = ElementV5
export type TElement = TElementV5
